import { ArrowRight, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { Notice, NoticeStatus } from "@/features/notices/types";
import { cn } from "@/lib/utils";

interface StatusMeta {
  label: string;
  /** soft pill / code-well tint */
  soft: string;
  /** status dot colour */
  dot: string;
  /** deadline figure colour */
  deadline: string;
  /** left accent bar (urgent notices only) */
  accent: boolean;
}

const STATUS_META: Record<NoticeStatus, StatusMeta> = {
  OPEN: {
    label: "Response due",
    soft: "bg-warning-soft text-warning",
    dot: "bg-primary",
    deadline: "text-warning",
    accent: true,
  },
  IN_PROGRESS: {
    label: "Awaiting IRS",
    soft: "bg-info-soft text-info",
    dot: "bg-info",
    deadline: "text-info",
    accent: false,
  },
  RESOLVED: {
    label: "Resolved",
    soft: "bg-success-soft text-success",
    dot: "bg-success",
    deadline: "text-success",
    accent: false,
  },
};

function formatDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

interface NoticeCardProps {
  notice: Notice;
  clientId: string;
  /** Opens the attached notice scan. */
  onViewDocument: (documentId: string) => void;
}

/**
 * A single IRS notice as a rich card (Calm Studio). Resolved notices collapse
 * to a compact single-line variant; open/in-progress notices show the deadline
 * block, step-progress tracker, and per-notice actions.
 */
export function NoticeCard({ notice, clientId, onViewDocument }: NoticeCardProps) {
  const meta = STATUS_META[notice.status];
  const detailHref = `/client/${clientId}/irs-notices/${notice.id}`;
  const description = notice.notes || notice.recommendedAction;

  // --- compact resolved card -------------------------------------------------
  if (notice.status === "RESOLVED") {
    return (
      <Card className="flex-row flex-wrap items-center gap-4 p-[18px_24px] opacity-95">
        <span
          className={cn(
            "font-heading flex size-12 shrink-0 items-center justify-center rounded-[14px] text-[13px] font-bold tracking-wide",
            meta.soft,
          )}
        >
          {notice.noticeCode}
        </span>
        <div className="flex min-w-0 flex-[1_1_280px] flex-col gap-0.5">
          <div className="flex flex-wrap items-center gap-2.5">
            <Link to={detailHref} className="text-foreground text-[15px] font-semibold hover:underline">
              {notice.noticeTitle}
            </Link>
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-[3px] text-xs font-semibold",
                meta.soft,
              )}
            >
              <Check className="size-3" /> {meta.label}
            </span>
          </div>
          <span className="text-muted-foreground text-[13px]">
            Tax year <span className="tabular-nums">{notice.taxYear || "—"}</span>
            {description ? ` · ${description}` : ""}
          </span>
        </div>
        {notice.document ? (
          <Button variant="outline" className="h-9" onClick={() => onViewDocument(notice.document!)}>
            View file
          </Button>
        ) : (
          <Button asChild variant="outline" className="h-9">
            <Link to={detailHref}>View file</Link>
          </Button>
        )}
      </Card>
    );
  }

  // --- full open / in-progress card ------------------------------------------
  const total = notice.steps.length;
  const completedCount = notice.steps.filter((s) => s.isCompleted).length;
  const nextStep = notice.steps.find((s) => !s.isCompleted);
  const caption = nextStep?.description || description;

  const overdue = notice.daysUntilDeadline !== null && notice.daysUntilDeadline < 0;
  const dayCount = notice.daysUntilDeadline !== null ? Math.abs(notice.daysUntilDeadline) : null;

  return (
    <Card className="relative gap-4 overflow-hidden p-[22px_24px]">
      {meta.accent && (
        <span aria-hidden className="bg-primary absolute inset-y-0 left-0 w-1" />
      )}

      <div className="flex flex-wrap items-start gap-4">
        <span
          className={cn(
            "font-heading flex size-14 shrink-0 items-center justify-center rounded-2xl text-[13px] font-bold tracking-wide",
            meta.soft,
          )}
        >
          {notice.noticeCode}
        </span>

        <div className="flex min-w-0 flex-[1_1_280px] flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2.5">
            <Link
              to={detailHref}
              className="text-foreground text-lg font-semibold tracking-tight hover:underline"
            >
              {notice.noticeTitle}
            </Link>
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-[3px] text-xs font-semibold",
                meta.soft,
              )}
            >
              <span className={cn("size-1.5 rounded-full", meta.dot)} /> {meta.label}
            </span>
          </div>
          {description && <span className="text-muted-foreground text-[13px]">{description}</span>}
          <div className="text-muted-foreground mt-1 flex flex-wrap gap-x-[18px] gap-y-1.5 text-[13px]">
            <span>
              Tax year{" "}
              <strong className="text-foreground font-semibold tabular-nums">
                {notice.taxYear || "—"}
              </strong>
            </span>
            <span>
              Received{" "}
              <strong className="text-foreground font-semibold tabular-nums">
                {formatDate(notice.receivedOn)}
              </strong>
            </span>
            <span>
              Owner{" "}
              <strong className="text-foreground font-semibold">
                {notice.assignedPractitionerName || "Unassigned"}
              </strong>
            </span>
          </div>
        </div>

        {dayCount !== null && (
          <div className="flex shrink-0 flex-col items-end gap-0.5 text-right">
            <span className="text-muted-foreground text-[11px] font-semibold tracking-wider uppercase">
              {overdue ? "Overdue by" : "Respond by"}
            </span>
            <span
              className={cn(
                "font-heading text-[26px] leading-none font-bold tracking-tight tabular-nums",
                overdue ? "text-destructive" : meta.deadline,
              )}
            >
              {dayCount} days
            </span>
            {notice.responseDeadline && (
              <span className="text-muted-foreground text-xs tabular-nums">
                {formatDate(notice.responseDeadline)}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3.5">
        <div className="text-muted-foreground flex items-center gap-2.5 text-[13px]">
          {total > 0 && (
            <span
              className="inline-flex gap-1"
              aria-label={`Response progress: ${completedCount} of ${total} steps`}
            >
              {notice.steps.map((step) => (
                <span
                  key={step.id}
                  className={cn(
                    "h-[5px] w-[26px] rounded-[3px]",
                    step.isCompleted
                      ? "bg-success"
                      : step.id === nextStep?.id
                        ? "bg-primary"
                        : "bg-border",
                  )}
                />
              ))}
            </span>
          )}
          {caption && <span className="min-w-0">{caption}</span>}
        </div>
        <div className="flex gap-2">
          {notice.document ? (
            <Button variant="outline" className="h-9" onClick={() => onViewDocument(notice.document!)}>
              View notice
            </Button>
          ) : (
            <Button asChild variant="outline" className="h-9">
              <Link to={detailHref}>View notice</Link>
            </Button>
          )}
          {notice.status === "OPEN" ? (
            <Button asChild className="bg-brand-panel hover:bg-brand-panel/90 h-9 text-white">
              <Link to={detailHref}>
                Continue response <ArrowRight />
              </Link>
            </Button>
          ) : (
            <Button asChild variant="outline" className="h-9">
              <Link to={detailHref}>Log IRS reply</Link>
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
